import React, { createContext, useContext, useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const PushNotificationContext = createContext();

export const usePushNotificationContext = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotificationContext must be used within a PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {
  const { isAuthenticated, currentUser } = useAuth();
  const user = currentUser; // Alias for consistency with hook

  // Pass auth to the hook
  const pushNotifications = usePushNotifications({ user, isAuthenticated });

  // Auto-subscribe when user logs in (if they were previously subscribed)
  useEffect(() => {
    if (isAuthenticated && user && pushNotifications.isSupported) {
      // Check if user was previously subscribed and auto-subscribe
      const wasSubscribed = localStorage.getItem('pushNotificationsEnabled');
      if (wasSubscribed === 'true' && pushNotifications.permission === 'granted' && !pushNotifications.isSubscribed) {
        pushNotifications.subscribe().catch(error => {
          console.log('Auto-subscription failed:', error);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, pushNotifications.isSupported, pushNotifications.permission, pushNotifications.isSubscribed]);

  // Provide auth status to hook
  const enhancedValue = {
    ...pushNotifications,
    isAuthenticated,
    user,
    // Additional context-specific methods
    enablePushNotifications: async () => {
      // Check auth before subscribing
      if (!isAuthenticated || !user) {
        // Guest visitor — silently bail out, banner should never call this for guests
        return false;
      }

      try {
        await pushNotifications.subscribe();
        localStorage.setItem('pushNotificationsEnabled', 'true');
        return true;
      } catch (error) {
        console.error('Failed to enable push notifications:', error);
        return false;
      }
    },
    disablePushNotifications: async () => {
      try {
        await pushNotifications.unsubscribe();
        localStorage.setItem('pushNotificationsEnabled', 'false');
        return true;
      } catch (error) {
        console.error('Failed to disable push notifications:', error);
        return false;
      }
    }
  };

  // Handle messages from service worker using BroadcastChannel
  useEffect(() => {
    const channel = new BroadcastChannel('push-notifications');

    const handleMessage = (event) => {
      if (!event.data) return;

      if (event.data.type === 'PUSH_RECEIVED') {
        console.log('FRONTEND RECEIVED PUSH MESSAGE:', event.data);

        // Show in-app toast for foreground notifications
        const { title, message, url, icon } = event.data.notification;

        toast.custom((t) => (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'} 
              max-w-md w-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-md 
              shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 dark:border-slate-800 
              rounded-xl pointer-events-auto flex overflow-hidden cursor-pointer
              transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.16)]`}
            onClick={() => {
              // Only navigate, do NOT dismiss the toast automatically
              if (url && url !== window.location.pathname) {
                window.location.href = url;
              }
            }}
          >
            {/* Left accent border */}
            <div className="w-1.5 bg-gradient-to-b from-primary-500 to-primary-600 dark:from-primary-400 dark:to-primary-500"></div>

            <div className="flex-1 w-0 p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-1">
                  <div className="h-11 w-11 rounded-full p-0.5 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-800 shadow-sm">
                    <img
                      className="h-full w-full rounded-full object-cover bg-white dark:bg-slate-900"
                      src={icon || '/favicon.png'}
                      alt="Notification Icon"
                    />
                  </div>
                </div>
                <div className="ml-3.5 flex-1">
                  <p className="text-[15px] font-semibold text-gray-900 dark:text-white leading-5">
                    {title}
                  </p>
                  <p className="mt-1.5 text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                    {message}
                  </p>
                </div>
              </div>
            </div>

            {/* Close Button Area */}
            <div className="flex border-l border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(t.id);
                }}
                className="w-12 flex items-center justify-center focus:outline-none group"
                aria-label="Close"
              >
                <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:text-slate-500 dark:group-hover:text-slate-300 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ), { duration: Infinity });
      }
    };

    channel.addEventListener('message', handleMessage);

    // Also keep the old listener just for NOTIFICATION_CLICK which still uses postMessage
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        const url = event.data.url;
        if (url && url !== window.location.pathname) {
          window.location.href = url;
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  const value = enhancedValue;

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};
